/**
 * RecordGamePage — record an already-played game together with its result in one
 * step, at /games/record.
 *
 * The division's fixtures are pre-built (round-robin), so you pick the GAME (a
 * fixture you're involved in that hasn't been proposed/recorded yet) from a
 * dropdown, add the single played date/time/venue, and enter the result. On
 * submit the fixture is claimed and lands in "played, awaiting approval"; the
 * opponent approves it to complete it.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { recordGame, fetchAvailableFixtures } from '../../store/slices/gameSlice';
import { fetchLeagueSeason } from '../../store/slices/leagueSlice';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import SlotEditor, {
  emptySlot,
  isSlotComplete,
  isDateOutsideSeason,
  type SlotInput,
} from '../../components/game/SlotEditor';
import ScoreEntry, { type ScoreEntryResult } from '../../components/game/ScoreEntry';
import FixtureSelectOptions from '../../components/game/FixtureSelectOptions';
import GameNotesField from '../../components/game/GameNotesField';

const RecordGamePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { availableFixtures, loading } = useSelector((state: RootState) => state.game);
  const { selectedLeagueId } = useSelector((state: RootState) => state.ui);
  const { currentSeason } = useSelector((state: RootState) => state.league);

  // Back/Cancel return to the real (league-scoped) Games page, not the bare
  // /games card list.
  const gamesHref = selectedLeagueId ? `/leagues/${selectedLeagueId}/games` : '/games';

  const [selectedGameId, setSelectedGameId] = useState('');
  const [slot, setSlot] = useState<SlotInput>(emptySlot());
  const [result, setResult] = useState<ScoreEntryResult>({
    setWins: { a: 0, b: 0 },
    winner: null,
    sets: [],
    hasErrors: false,
    isComplete: false,
    retirement: { isRetirement: false, retiredSide: null },
  });
  const [notes, setNotes] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { resolved: tenantResolved } = useActiveLeagueTenant(undefined);

  useEffect(() => {
    if (tenantResolved) {
      dispatch(fetchAvailableFixtures());
    }
  }, [dispatch, tenantResolved]);

  const fixture = useMemo(
    () => availableFixtures.find(f => f.id === selectedGameId),
    [availableFixtures, selectedGameId],
  );
  // Load the fixture's season so the played date can be constrained to it.
  useEffect(() => {
    if (fixture?.seasonId && selectedLeagueId) {
      dispatch(fetchLeagueSeason({ leagueId: selectedLeagueId, seasonId: fixture.seasonId }));
    }
  }, [dispatch, fixture?.seasonId, selectedLeagueId]);

  const toISODate = (d: string | Date): string => new Date(d).toISOString().slice(0, 10);
  const seasonBounds = useMemo(() => {
    if (currentSeason && fixture && currentSeason.id === fixture.seasonId) {
      return {
        minDate: toISODate(currentSeason.startDate),
        maxDate: toISODate(currentSeason.endDate),
      };
    }
    return { minDate: undefined, maxDate: undefined };
  }, [currentSeason, fixture]);

  const teamAId = fixture?.teamIds[0];
  const teamBId = fixture?.teamIds[1];
  const teamALabel = fixture
    ? fixture.teamNames?.[teamAId as string] ?? teamAId ?? ''
    : 'Your team';
  const teamBLabel = fixture ? fixture.teamNames?.[teamBId as string] ?? teamBId ?? '' : 'Opponent';

  const handleResultChange = useCallback((r: ScoreEntryResult) => setResult(r), []);

  const validate = (): string | null => {
    if (!selectedGameId || !fixture) return 'Please select a game.';
    if (!isSlotComplete(slot)) {
      return 'Enter the date, time, venue and court/address where the game was played.';
    }
    if (isDateOutsideSeason(slot.date, seasonBounds.minDate, seasonBounds.maxDate)) {
      return `The played date must fall within the season (${seasonBounds.minDate} – ${seasonBounds.maxDate}).`;
    }
    if (result.retirement.isRetirement) {
      if (!result.retirement.retiredSide) return 'Please choose which team retired.';
      return null;
    }
    if (result.hasErrors) return 'Please fix the score validation errors.';
    if (!result.isComplete) return 'Enter the game scores — a winner must be determinable.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError(null);

    const a = teamAId as string;
    const b = teamBId as string;
    const { isRetirement, retiredSide } = result.retirement;
    const retiredTeamId = isRetirement ? (retiredSide === 'a' ? a : b) : undefined;
    try {
      const game = await dispatch(
        recordGame({
          gameId: selectedGameId,
          scheduledDate: slot.date,
          scheduledTime: slot.time,
          venue: { name: slot.venueName, address: slot.venueAddress },
          result: {
            scores: { [a]: result.setWins.a, [b]: result.setWins.b },
            sets: result.sets.map(s => ({ [a]: s.a, [b]: s.b })),
            ...(retiredTeamId ? { retiredTeamId } : {}),
          },
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        }),
      ).unwrap();
      navigate(`/games/${game.id}`);
    } catch (submitErr) {
      setSubmitError(submitErr instanceof Error ? submitErr.message : String(submitErr));
    }
  };

  const isSubmitting = loading;
  const noFixtures = !loading && availableFixtures.length === 0;

  return (
    <div className="max-w-2xl mx-auto">
      <Link to={gamesHref} className="text-sm text-primary-600 hover:text-primary-700">
        &larr; Back to Games
      </Link>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">Record a game</h1>
      <p className="mt-1 text-gray-600">
        Already played a game? Pick it below and enter the result in one step. The result is sent to
        your opponent to approve — once they do, the game is completed and points are awarded.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {submitError && (
          <div className="mt-4 rounded-md bg-danger-50 p-4" role="alert">
            <p className="text-sm text-danger-600">{submitError}</p>
          </div>
        )}

        {/* Step 1: Pick the game (fixture) */}
        <section className="mt-6 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">1 · Game</h2>
          <label htmlFor="fixture" className="mt-3 block text-sm font-medium text-gray-700">
            Which game?
          </label>
          <select
            id="fixture"
            value={selectedGameId}
            onChange={e => setSelectedGameId(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
            disabled={loading || noFixtures}
            aria-label="Game to record"
          >
            <option value="">{loading ? 'Loading games…' : 'Select a game…'}</option>
            <FixtureSelectOptions fixtures={availableFixtures} />
          </select>
          <p className="mt-1 text-xs text-gray-400">
            Only your games that haven't been proposed or recorded yet are listed.
          </p>
          {noFixtures && (
            <p className="mt-1 text-xs text-danger-600">
              You have no games left to record — they've all been proposed, recorded, or played.
            </p>
          )}
        </section>

        {/* Step 2: When & where it was played */}
        <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">
            2 · When &amp; where it was played
          </h2>
          <SlotEditor
            index={0}
            slot={slot}
            onChange={setSlot}
            showErrors={attemptedSubmit}
            minDate={seasonBounds.minDate}
            maxDate={seasonBounds.maxDate}
          />
        </section>

        {/* Step 3: Result */}
        <section className="mt-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-1 px-1">3 · Result</h2>
          <ScoreEntry
            pointSystem={fixture?.divisionPointSystem}
            teamALabel={teamALabel}
            teamBLabel={teamBLabel}
            onChange={handleResultChange}
          />
        </section>

        {/* Step 4: Notes */}
        <GameNotesField
          label="4 · Notes"
          value={notes}
          onChange={setNotes}
          placeholder="e.g. Great match; agreed the score on the day."
        />

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-3">
          <Link
            to={gamesHref}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || !selectedGameId}
            className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Recording…' : 'Record & send for approval'}
          </button>
        </div>

        <div className="mt-4 rounded-md bg-primary-50 text-primary-800 text-sm p-3">
          The game is created as <strong>Played · Awaiting approval</strong>. Your opponent approves
          it under <strong>Games</strong> (or on their Home page); once approved it becomes{' '}
          <strong>Completed</strong> and points are awarded.
        </div>
      </form>
    </div>
  );
};

export default RecordGamePage;
